// src/hooks/useLiveStreamWebSocket.ts
import { useEffect, useRef, useCallback, useState } from 'react';
import { Client, type IMessage } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { config } from '../config';
import { activeProfileHeader } from '../services/viewerProfileService';
import unifiedAuth from '../utils/unifiedAuth';

// Types for WebSocket messages
export interface StreamStats {
  streamId: string;
  currentViewers: number;
  totalViews: number;
  likeCount: number;
  commentCount: number;
  status: string;
  timestamp: string;
}

export interface ChatMessage {
  id: string;
  streamId: string;
  userId?: string;
  username: string;
  userAvatar?: string;
  message: string;
  isFromHost: boolean;
  isFromModerator: boolean;
  isPinned: boolean;
  isHighlighted: boolean;
  streamTimestampSeconds?: number;
  timestamp: string;
  type: 'CHAT' | 'SYSTEM' | 'JOIN' | 'LEAVE' | 'PIN' | 'UNPIN' | 'DELETE' | 'HOST_MESSAGE';
}

export interface ReactionEvent {
  streamId: string;
  userId?: string;
  username: string;
  reactionType: 'LIKE' | 'HEART' | 'FIRE' | 'CLAP' | 'LAUGH' | 'WOW' | 'SAD';
  isAdding: boolean;
  timestamp: string;
}

export interface ViewerEvent {
  streamId: string;
  sessionId: string;
  userId?: string;
  username?: string;
  eventType: 'JOIN' | 'LEAVE';
  currentViewers: number;
}

interface UseLiveStreamWebSocketOptions {
  streamId: string | null;
  onStatsUpdate?: (stats: StreamStats) => void;
  onChatMessage?: (message: ChatMessage) => void;
  onReaction?: (reaction: ReactionEvent) => void;
  onViewerUpdate?: (event: ViewerEvent) => void;
  /**
   * A message the server addressed to this viewer alone, rather than to the room.
   *
   * Refusals arrive here: too young for an 18-rated stream's chat, chat turned off, not
   * signed in. They used to be returned through the handler's `@SendTo`, so they were
   * broadcast to every viewer - and the person refused is gated out of the chat topic, so
   * they were the one person who never saw their own refusal.
   */
  onSenderNotice?: (message: ChatMessage) => void;
  onConnectionChange?: (connected: boolean) => void;
  autoJoin?: boolean;
}

export function useLiveStreamWebSocket({
  streamId,
  onStatsUpdate,
  onChatMessage,
  onReaction,
  onViewerUpdate,
  onSenderNotice,
  onConnectionChange,
  autoJoin = true,
}: UseLiveStreamWebSocketOptions) {
  const clientRef = useRef<Client | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  
  // Use refs for callbacks to avoid reconnection loops
  const callbacksRef = useRef({
    onStatsUpdate,
    onChatMessage,
    onReaction,
    onViewerUpdate,
    onSenderNotice,
    onConnectionChange,
  });
  
  // Update callback refs when they change
  useEffect(() => {
    callbacksRef.current = {
      onStatsUpdate,
      onChatMessage,
      onReaction,
      onViewerUpdate,
      onSenderNotice,
      onConnectionChange,
    };
  }, [onStatsUpdate, onChatMessage, onReaction, onViewerUpdate, onSenderNotice, onConnectionChange]);

  // Initialize WebSocket connection
  const connect = useCallback(() => {
    // Don't connect if no streamId or already connected
    if (!streamId || clientRef.current?.connected) {
      return;
    }

    // WebSocket endpoint is at /api/ws/live (includes context path)
    const wsUrl = `${config.apiBaseUrl.replace('/api', '')}/api/ws/live`;

    const client = new Client({
      webSocketFactory: () => new SockJS(wsUrl),
      // Built on every connect attempt, not captured once at construction.
      //
      // connectHeaders is a fixed property, so the token read above was reused by
      // every automatic reconnect for the life of the client. A day-long token
      // survives that; a short-lived one does not - any reconnect after expiry
      // authenticates with a dead token and the stream silently stops, in a retry
      // loop that can never succeed. beforeConnect runs per attempt and can await,
      // so an expired token is refreshed rather than merely re-sent.
      beforeConnect: async () => {
        const fresh = await unifiedAuth.ensureFreshToken();
        client.connectHeaders = fresh ? { Authorization: `Bearer ${fresh}` } : {};
      },
      debug: () => {}, // Suppress debug logs
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect: () => {
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttemptsRef.current = 0;
        callbacksRef.current.onConnectionChange?.(true);

        // Subscribe to stream topics
        // Stats updates
        client.subscribe(`/topic/stream/${streamId}/stats`, (message: IMessage) => {
          try {
            const stats: StreamStats = JSON.parse(message.body);
            callbacksRef.current.onStatsUpdate?.(stats);
          } catch (e) {
            console.error('[WebSocket] Error parsing stats:', e);
          }
        });

        // Chat messages.
        //
        // The profile goes on the SUBSCRIBE frame as a native header: a STOMP frame has
        // no HTTP headers, and the server gates this subscription by profile - a profile
        // capped below the account should not receive an 18-rated stream's chat at all.
        client.subscribe(`/topic/stream/${streamId}/chat`, (message: IMessage) => {
          try {
            const chatMessage: ChatMessage = JSON.parse(message.body);
            callbacksRef.current.onChatMessage?.(chatMessage);
          } catch (e) {
            console.error('[WebSocket] Error parsing chat:', e);
          }
        }, activeProfileHeader());

        // Reactions
        client.subscribe(`/topic/stream/${streamId}/reactions`, (message: IMessage) => {
          try {
            const reaction: ReactionEvent = JSON.parse(message.body);
            callbacksRef.current.onReaction?.(reaction);
          } catch (e) {
            console.error('[WebSocket] Error parsing reaction:', e);
          }
        });

        // Viewer updates
        client.subscribe(`/topic/stream/${streamId}/viewers`, (message: IMessage) => {
          try {
            const viewerEvent: ViewerEvent = JSON.parse(message.body);
            callbacksRef.current.onViewerUpdate?.(viewerEvent);
          } catch (e) {
            console.error('[WebSocket] Error parsing viewer event:', e);
          }
        });

        // Notices addressed to this viewer alone.
        //
        // Not a stream topic: /user/... is resolved per session, so this delivers only to
        // this connection. It is where a refusal to post arrives - and it has to be a
        // separate destination, because an under-age viewer cannot subscribe to the chat
        // topic at all, which is exactly where their refusal used to be sent.
        client.subscribe('/user/queue/stream-errors', (message: IMessage) => {
          try {
            const notice: ChatMessage = JSON.parse(message.body);
            callbacksRef.current.onSenderNotice?.(notice);
          } catch (e) {
            console.error('[WebSocket] Error parsing sender notice:', e);
          }
        });

        // Auto-join the stream
        if (autoJoin) {
          client.publish({
            destination: `/app/stream/${streamId}/join`,
            body: JSON.stringify({}),
          });
        }
      },
      onDisconnect: () => {
        setIsConnected(false);
        callbacksRef.current.onConnectionChange?.(false);
      },
      onStompError: (frame) => {
        console.error('[WebSocket] STOMP error:', frame.headers['message']);
        setConnectionError(frame.headers['message'] || 'Connection error');
        
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++;
        }
      },
      onWebSocketError: (event) => {
        console.error('[WebSocket] WebSocket error:', event);
        setConnectionError('WebSocket connection failed');
      },
    });

    clientRef.current = client;
    client.activate();
  }, [streamId, autoJoin]);

  // Disconnect
  const disconnect = useCallback(() => {
    if (clientRef.current?.connected && streamId) {
      // Send leave message before disconnecting
      clientRef.current.publish({
        destination: `/app/stream/${streamId}/leave`,
        body: JSON.stringify({}),
      });
      clientRef.current.deactivate();
    }
    clientRef.current = null;
    setIsConnected(false);
  }, [streamId]);

  // Join stream (notify backend of viewer joining)
  const joinStream = useCallback(() => {
    if (clientRef.current?.connected && streamId) {
      clientRef.current.publish({
        destination: `/app/stream/${streamId}/join`,
        body: JSON.stringify({}),
      });
    }
  }, [streamId]);

  // Leave stream
  const leaveStream = useCallback(() => {
    if (clientRef.current?.connected && streamId) {
      clientRef.current.publish({
        destination: `/app/stream/${streamId}/leave`,
        body: JSON.stringify({}),
      });
    }
  }, [streamId]);

  // Send chat message
  const sendChatMessage = useCallback((message: string) => {
    if (clientRef.current?.connected && streamId && message.trim()) {
      clientRef.current.publish({
        destination: `/app/stream/${streamId}/chat`,
        body: JSON.stringify({
          streamId,
          message: message.trim(),
          type: 'CHAT',
        }),
        // Same native header on the SEND frame: posting is gated by profile too.
        headers: activeProfileHeader(),
      });
    }
  }, [streamId]);

  // Send reaction
  const sendReaction = useCallback((reactionType: ReactionEvent['reactionType'], isAdding: boolean = true) => {
    if (clientRef.current?.connected && streamId) {
      clientRef.current.publish({
        destination: `/app/stream/${streamId}/reaction`,
        body: JSON.stringify({
          streamId,
          reactionType,
          isAdding,
        }),
      });
    }
  }, [streamId]);

  // Request current stats
  const requestStats = useCallback(() => {
    if (clientRef.current?.connected && streamId) {
      clientRef.current.publish({
        destination: `/app/stream/${streamId}/stats`,
        body: JSON.stringify({}),
      });
    }
  }, [streamId]);

  // Connect on mount, disconnect on unmount
  useEffect(() => {
    if (streamId) {
      connect();
    }

    return () => {
      disconnect();
    };
  }, [streamId, connect, disconnect]);

  // Handle visibility change (reconnect when tab becomes visible)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !isConnected && streamId) {
        connect();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connect, isConnected, streamId]);

  return {
    isConnected,
    connectionError,
    connect,
    disconnect,
    sendChatMessage,
    sendReaction,
    joinStream,
    leaveStream,
    requestStats,
  };
}

export default useLiveStreamWebSocket;
